import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/** Who a scheduled report is read as: the owner (one version for everyone) or each recipient. */
export type ReportScheduleRunAsModel = 'owner' | 'recipient';
/**
 * `oneTime` (PD-17455) is a report sent once from its page with Share → Send
 * to someone: kept like a schedule so its send has the same History, but it
 * never repeats and cannot be edited, paused or resumed.
 */
export type ReportScheduleStatusModel = 'active' | 'paused' | 'oneTime';
/** One PDF and one workbook for the whole email, a file per report, or none. */
export type ReportScheduleAttachmentModeModel = 'combined' | 'separate' | 'none';

/**
 * PD-17398 — a scheduled report email: one or more reports
 * (`report_schedule_item`) sent on a repeating schedule to people, teams,
 * roles and addresses (`report_schedule_recipient`).
 *
 * The timing is a cron expression in the schedule's own time zone, and
 * `nextRunAt` (UTC) is when it is next due. The minute scheduler claims a
 * due schedule by moving `nextRunAt` forward with a compare-and-set, so two
 * workers never send the same run. Report schedules have their own tables,
 * not the automation trigger table, so they never appear in the automation
 * builder.
 */
@Index('idx_report_schedule_due', ['status', 'nextRunAt'])
@Index('idx_report_schedule_owner', ['fkUserIdOwner'])
@Entity('report_schedule')
export class ReportScheduleEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 200 })
	public name!: string;

	/** Tenant `user._id` of the owner: the only person who can delete it or change Run as. */
	@Column('int', { name: 'fk_user__id_owner', unsigned: true })
	public fkUserIdOwner!: number;

	@Column('varchar', { name: 'runAs', length: 20, default: 'owner' })
	public runAs!: ReportScheduleRunAsModel;

	@Column('varchar', { name: 'status', length: 20, default: 'active' })
	public status!: ReportScheduleStatusModel;

	/** Five-field cron in `timeZone`, e.g. `0 6 * * 1` for Mondays at 6:00 AM. */
	@Column('varchar', { name: 'cronExpression', length: 100 })
	public cronExpression!: string;

	/** IANA zone the cron is written in, e.g. America/Detroit. */
	@Column('varchar', { name: 'timeZone', length: 64 })
	public timeZone!: string;

	/** When it is next due, UTC. Null while paused. */
	@Column('datetime', { name: 'nextRunAt', nullable: true })
	public nextRunAt!: Date | null;

	/** Email subject; placeholders {schedule} {report} {period} {start} {end} {date}. Null uses the default. */
	@Column('varchar', { name: 'subjectTemplate', length: 300, nullable: true })
	public subjectTemplate!: string | null;

	/** Attachment file name, same placeholders. Null uses the default. */
	@Column('varchar', { name: 'fileNameTemplate', length: 200, nullable: true })
	public fileNameTemplate!: string | null;

	@Column('varchar', { name: 'attachmentMode', length: 20, default: 'combined' })
	public attachmentMode!: ReportScheduleAttachmentModeModel;

	@Column({ type: 'boolean', name: 'attachPdf', width: 1, default: 1 })
	public attachPdf!: boolean;

	@Column({ type: 'boolean', name: 'attachExcel', width: 1, default: 1 })
	public attachExcel!: boolean;

	@Column({ type: 'boolean', name: 'attachCsv', width: 1, default: 0 })
	public attachCsv!: boolean;

	/** Money amounts in the email and PDF: `auto` or `exact` (Excel cells are always exact). */
	@Column('varchar', { name: 'amountsMode', length: 10, default: 'auto' })
	public amountsMode!: string;

	/**
	 * PD-17458 — rows each report's PDF lists (100, 250, 500, 1000, or 0 for
	 * no limit up to the ceiling). Null reads as the default, 100.
	 */
	@Column('int', { name: 'pdfRowLimit', nullable: true })
	public pdfRowLimit?: number | null;

	/**
	 * PD-17428 — the moving date range every report uses unless it has its
	 * own (`report_schedule_item.ownRange`), e.g. `lastWeek`.
	 */
	@Column('varchar', { name: 'periodType', length: 40, default: 'lastWeek' })
	public periodType!: string;

	/** Send nothing when every report in it is empty. */
	@Column({ type: 'boolean', name: 'skipWhenEmpty', width: 1, default: 1 })
	public skipWhenEmpty!: boolean;

	@Column('datetime', { name: 'lastRunAt', nullable: true })
	public lastRunAt!: Date | null;

	/** The last run's status (see report_schedule_run.status), for the Scheduled list. */
	@Column('varchar', { name: 'lastRunStatus', length: 20, nullable: true })
	public lastRunStatus!: string | null;

	/** PD-17455 — a one-time send's note from the sender, shown at the top of the email. */
	@Column('varchar', { name: 'message', length: 1000, nullable: true })
	public message!: string | null;
}
